import { auth } from "../config/firebase";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  type DocumentData,
} from "firebase/firestore";

export type UserProfile = {
  fullName?: string | null;
  rollNo?: string | null;
  points?: number;
};

async function getDb() {
  try {
    return getFirestore();
  } catch {
    // dynamic import fallback
    const mod = await import("firebase/firestore");
    return mod.getFirestore();
  }
}

export async function fetchUserProfile(
  uid: string
): Promise<UserProfile | null> {
  try {
    const db = await getDb();
    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data() as DocumentData as UserProfile;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to fetch user profile", e);
    return null;
  }
}

export async function upsertUserProfile(
  uid: string,
  payload: Partial<UserProfile>
): Promise<void> {
  try {
    const db = await getDb();
    const ref = doc(db, "users", uid);
    // Include the authenticated user's email if available so the document
    // stores an associated email address.
    const currentEmail = auth?.currentUser?.email ?? null;
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      // New document: initialize points to 0 and createdAt timestamp.
      const toWrite = {
        ...(payload || {}),
        email: currentEmail ?? null,
        points: 0,
        createdAt: serverTimestamp(),
      } as any;
      await setDoc(ref, toWrite, { merge: true });
    } else {
      const toWrite = { ...(payload || {}) } as any;
      if (currentEmail && !toWrite.email) toWrite.email = currentEmail;
      await setDoc(ref, toWrite, { merge: true });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to upsert user profile", e);
    throw e;
  }
}

export default { fetchUserProfile, upsertUserProfile };
